import { useState, useCallback } from 'react';
import { useChatStore } from '@/store/useChatStore';
import { useAuthStore } from '@/store/useAuthStore';
import { streamDiscoveryChat, extractMemberProposal, cleanMessageContent } from '@/lib/anthropic';
import { api, VoucherRequiredError } from '@/lib/api';
import type { ChatMessage } from '@/types';

export function useChat(
  sessionId: string,
  intent: 'discovery' | 'team-consult' | 'member-consult',
  params?: { memberId?: string },
) {
  const { sessions, appendMessage, updateLastAssistantMessage, setMessageProposal, updateSessionTitle } =
    useChatStore();
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginRequired, setLoginRequired] = useState(false);
  const [voucherRequired, setVoucherRequired] = useState(false);

  const session = sessions.find((s) => s.id === sessionId);
  const messages = session?.messages ?? [];

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isStreaming || !session) return;
      if (!useAuthStore.getState().user) {
        setLoginRequired(true);
        return;
      }

      setError(null);

      // Auto-title from first user message
      if (messages.length === 0 && !session.title) {
        updateSessionTitle(sessionId, text.trim().slice(0, 60));
      }

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: text.trim(),
        timestamp: new Date().toISOString(),
      };
      appendMessage(userMessage);

      const assistantId = crypto.randomUUID();
      const assistantMessage: ChatMessage = {
        id: assistantId,
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
      };
      appendMessage(assistantMessage);

      setIsStreaming(true);

      const apiMessages = [
        ...messages.map((m) => ({
          role: m.role as 'user' | 'assistant',
          content: cleanMessageContent(m.content),
        })),
        { role: 'user' as const, content: text.trim() },
      ];

      await streamDiscoveryChat(
        '', // key resolved server-side
        apiMessages,
        (visibleContent) => {
          updateLastAssistantMessage(visibleContent);
        },
        (fullText) => {
          const cleanText = cleanMessageContent(fullText);
          updateLastAssistantMessage(cleanText);

          // Persist completed assistant message to server (streaming stub was never synced)
          if (useAuthStore.getState().user && cleanText) {
            api.sessions
              .appendMessage(sessionId, {
                role: 'assistant',
                content: cleanText,
                timestamp: assistantMessage.timestamp,
              })
              .catch(console.error);
          }

          if (intent === 'discovery') {
            const proposal = extractMemberProposal(fullText);
            if (proposal) {
              setMessageProposal(assistantId, proposal);
            }
          }

          setIsStreaming(false);
        },
        (err) => {
          if (err instanceof VoucherRequiredError) {
            setVoucherRequired(true);
            updateLastAssistantMessage('');
            setIsStreaming(false);
            return;
          }
          const msg =
            err.message.includes('401') || err.message.includes('authentication')
              ? 'Ungültiger API Key. Bitte überprüfe deine Einstellungen.'
              : `Fehler: ${err.message}`;
          updateLastAssistantMessage(msg);
          setError(msg);
          setIsStreaming(false);
        },
        intent,
        params,
      );
    },
    [
      messages, isStreaming, session, sessionId, intent, params,
      appendMessage, updateLastAssistantMessage, setMessageProposal, updateSessionTitle,
    ],
  );

  return {
    messages,
    isStreaming,
    error,
    sendMessage,
    loginRequired,
    dismissLoginPrompt: () => setLoginRequired(false),
    voucherRequired,
    dismissVoucherPrompt: () => setVoucherRequired(false),
  };
}
